import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { elakay_API } from "../../../config";
import { buildSandboxDocument, frameOrigins, stylesheetOriginsFromMarkup, FRAME_MESSAGE_SOURCE, PARENT_MESSAGE_SOURCE } from "./howWeDoItFrameDocument";
import { buildVideoEmbedUrl, isValidVideoRef } from "./howWeDoItVideoUrl";
import { deviceForWidth } from "./blockLayout";
import { DESIGNER_MESSAGE_KIND } from "./howWeDoItDesigner";
import "./howWeDoItSandboxedFrame.css";
import { elakayAutoText } from "../dataElakay";

// Clones every currently-loaded stylesheet (<link rel="stylesheet"> and
// <style>, dev AND prod builds alike) from the main document into the
// srcDoc's own <head>, so contentBlocks render with the same design-system
// CSS as the rest of the site. Only used for content written for Elakay
// (see shouldUseAppStyles in howWeDoItFrameDocument.js).
const collectAppStylesheetMarkup = () => {
  if (typeof document === "undefined") return "";
  return [...document.querySelectorAll('link[rel="stylesheet"], style')]
    .filter((node) => !node.closest("[data-hwdi-detached-clone]"))
    .map((node) => node.outerHTML)
    .join("\n");
};

const frameLabels = () => ({
  watchVideo: elakayAutoText("howWeDoIt_video_watch", "Regarder la video"),
  videoUnavailable: elakayAutoText("howWeDoIt_video_unavailable", "Cette video est indisponible (adresse non reconnue)."),
  embedBlocked: elakayAutoText("howWeDoIt_frame_embedBlocked", "Contenu integre bloque : cette source n'est pas autorisee."),
  mediaError: elakayAutoText("howWeDoIt_frame_mediaError", "Ce media n'a pas pu etre charge."),
  openOriginal: elakayAutoText("howWeDoIt_video_openOriginal", "Ouvrir le lien"),
});

// Which block layout (desktop / tablet / mobile) the visitor's screen uses.
// The frame's own width cannot tell (it sits inside the page's columns).
const useViewportDevice = (enabled) => {
  const read = () => (typeof window !== "undefined" ? deviceForWidth(window.innerWidth) : "desktop");
  const [device, setDevice] = useState(read);
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;
    const onResize = () => setDevice(read());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [enabled]);
  return device;
};

const MAX_SLOTS = 60;
const finite = (value, max = 100000) => (Number.isFinite(value) && Math.abs(value) <= max ? value : null);

// Slot data comes from inside the sandbox (author scripts could forge it):
// only an allow-listed provider + validated id ever becomes a player, built
// by Elakay's own code, positioned inside the frame's own box.
const sanitizeSlots = (slots) =>
  (Array.isArray(slots) ? slots : [])
    .slice(0, MAX_SLOTS)
    .map((slot) => {
      const ref = { provider: slot?.provider, id: String(slot?.id || ""), hash: String(slot?.hash || ""), start: Number(slot?.start) || 0 };
      if (!isValidVideoRef(ref)) return null;
      const x = finite(slot.x);
      const y = finite(slot.y, 1000000);
      const w = finite(slot.w, 6000);
      const h = finite(slot.h, 6000);
      if (x === null || y === null || w === null || h === null || w < 2 || h < 2 || !slot.visible) return null;
      return {
        key: String(slot.key || `${ref.provider}-${ref.id}`).slice(0, 120),
        ref,
        title: String(slot.title || "").slice(0, 200),
        autoplay: !!slot.autoplay,
        muted: !!slot.muted,
        controls: slot.controls !== false,
        x,
        y,
        w,
        h,
      };
    })
    .filter(Boolean);

const scrollContainerOf = (element) => {
  let node = element?.parentElement;
  const view = element?.ownerDocument?.defaultView;
  while (node && view) {
    const style = view.getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1) return node;
    node = node.parentElement;
  }
  return element?.ownerDocument?.scrollingElement || null;
};

/**
 * Renders a How We Do It document inside a sandboxed iframe -- the one place
 * the tutorial's own JavaScript ever runs. `sandbox` never contains
 * allow-same-origin (that single token would let the page reach Elakay's
 * origin). Used identically by the Editor preview (and its detached window),
 * the Console's secure preview and the public tutorial page.
 *
 * - Height follows the content (no fixed 900px box, no double scroll).
 * - YouTube/Vimeo players are drawn by this component over the slots the
 *   page reserves for them (players refuse to run inside the sandbox).
 * - Links are opened by this component: same-site addresses through the
 *   app router (linkMode="navigate"), everything else in a new tab.
 *
 * Exposes `scrollToBlock(blockId)` via ref.
 */
const HowWeDoItSandboxedFrame = forwardRef(
  (
    {
      tutorial,
      height,
      initialHeight,
      minHeight = 240,
      maxHeight = 20000,
      title = "How We Do It preview",
      linkMode = "newTab",
      className = "",
      // Editor preview only: the device being edited, the visual editing
      // options ({ edit, typeLabel }), the selected block and the callback
      // receiving the frame's selection / change reports.
      device: deviceProp = null,
      designer = null,
      selectedBlockId = null,
      onDesignerEvent = null,
    },
    ref
  ) => {
    const [errorCount, setErrorCount] = useState(0);
    const [frameHeight, setFrameHeight] = useState(initialHeight || height || 600);
    const [slots, setSlots] = useState([]);
    const iframeRef = useRef(null);
    const wrapRef = useRef(null);
    const navigation = useContext(UNSAFE_NavigationContext);
    const viewportDevice = useViewportDevice(!deviceProp);
    const device = deviceProp || viewportDevice;
    // The selection is sent by message (no reload); a rebuilt document
    // starts with the current one.
    const selectedRef = useRef(selectedBlockId);
    selectedRef.current = selectedBlockId;
    const designerEventRef = useRef(onDesignerEvent);
    designerEventRef.current = onDesignerEvent;
    const designEdit = designer ? designer.edit !== false : false;
    const typeLabel = designer?.typeLabel;
    const unlockedIds = designer?.unlockedIds || [];
    const unlockedIdsKey = unlockedIds.join("|");

    const built = useMemo(() => {
      const appStylesMarkup = collectAppStylesheetMarkup();
      const { appOrigin, apiOrigin } = frameOrigins(elakay_API);
      return buildSandboxDocument(tutorial || {}, {
        appStylesMarkup,
        appOrigin,
        apiOrigin,
        styleOrigins: stylesheetOriginsFromMarkup(appStylesMarkup),
        labels: frameLabels(),
        device,
        designer: designer ? { edit: designEdit, selectedId: selectedRef.current, typeLabel, unlockedIds } : null,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tutorial, device, !!designer, designEdit, typeLabel, unlockedIdsKey]);

    // Typing in the editor must not reload the frame on every keystroke:
    // the first document renders at once, later ones after a short pause.
    const [srcDoc, setSrcDoc] = useState(built.html);
    const firstRenderRef = useRef(true);
    useEffect(() => {
      if (firstRenderRef.current) {
        firstRenderRef.current = false;
        setSrcDoc(built.html);
        return undefined;
      }
      const timer = setTimeout(() => setSrcDoc(built.html), 250);
      return () => clearTimeout(timer);
    }, [built.html]);

    useImperativeHandle(
      ref,
      () => ({
        scrollToBlock: (blockId) => {
          iframeRef.current?.contentWindow?.postMessage({ source: PARENT_MESSAGE_SOURCE, action: "scrollToBlock", blockId }, "*");
        },
        // Editor: asks the frame where a block is (to switch it to free
        // position without moving it); the answer comes as a designer event.
        measureBlock: (blockId, request = "") => {
          iframeRef.current?.contentWindow?.postMessage({ source: PARENT_MESSAGE_SOURCE, action: "designer:measure", blockId, request }, "*");
        },
      }),
      []
    );

    const postToFrame = useCallback((message) => {
      iframeRef.current?.contentWindow?.postMessage({ source: PARENT_MESSAGE_SOURCE, ...message }, "*");
    }, []);

    // Selection chosen in the editor (a block card opened): shown in the
    // frame and brought into view, without reloading the frame.
    const lastSentSelectionRef = useRef(selectedBlockId);
    useEffect(() => {
      if (!designer || lastSentSelectionRef.current === selectedBlockId) return;
      lastSentSelectionRef.current = selectedBlockId;
      const visible = iframeRef.current && iframeRef.current.getClientRects().length > 0;
      postToFrame({ action: "designer:select", blockId: selectedBlockId, reveal: !!visible });
    }, [designer, selectedBlockId, postToFrame]);

    // reveal: the editor showing the block being edited -- only the preview
    // pane may scroll for that, never the whole page (the form would jump).
    const scrollToFrameOffset = useCallback((offsetY, reveal = false) => {
      const frame = iframeRef.current;
      if (!frame) return;
      const container = scrollContainerOf(frame);
      if (!container) return;
      if (reveal && container === frame.ownerDocument.scrollingElement) return;
      const frameRect = frame.getBoundingClientRect();
      const frameTop = frameRect.top;
      // The editor may show the frame scaled down (device preview).
      const scale = frame.offsetHeight ? frameRect.height / frame.offsetHeight : 1;
      const containerTop = container === frame.ownerDocument.scrollingElement ? 0 : container.getBoundingClientRect().top;
      const target = container.scrollTop + frameTop - containerTop + Number(offsetY || 0) * scale - 80;
      container.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
    }, []);

    const openLink = useCallback(
      (data) => {
        const view = iframeRef.current?.ownerDocument?.defaultView || window;
        // Only follow a link the visitor actually clicked (a script inside
        // the page cannot redirect the site on its own).
        const activation = view.navigator?.userActivation;
        if (activation && !activation.isActive) return;
        let url;
        try {
          url = new URL(String(data?.href || ""), view.location.href);
        } catch {
          return;
        }
        if (!/^(https?|mailto|tel):$/.test(url.protocol)) return;
        const sameSite = url.origin === view.location.origin;
        if (sameSite && linkMode === "navigate" && !data?.newTab && !data?.download && navigation?.navigator) {
          navigation.navigator.push(`${url.pathname}${url.search}${url.hash}`);
          return;
        }
        if (url.protocol === "mailto:" || url.protocol === "tel:") {
          view.location.href = url.href;
          return;
        }
        view.open(url.href, "_blank", "noopener,noreferrer");
      },
      [linkMode, navigation]
    );

    useEffect(() => {
      setErrorCount(0);
      const frame = iframeRef.current;
      const view = frame?.ownerDocument?.defaultView || window;
      const onMessage = (event) => {
        // Only messages from THIS frame's own document (an opaque origin:
        // the string "null"), with our own message shape.
        if (event.source !== iframeRef.current?.contentWindow) return;
        if (event.data?.source !== FRAME_MESSAGE_SOURCE) return;
        const { kind, data } = event.data;
        if (kind === "error") setErrorCount((count) => count + 1);
        else if (kind === "layout") {
          const reported = Number(data?.height);
          if (Number.isFinite(reported) && reported > 0) {
            const next = Math.min(maxHeight, Math.max(minHeight, Math.ceil(reported)));
            setFrameHeight((current) => (Math.abs(current - next) >= 1 ? next : current));
          }
          setSlots(sanitizeSlots(data?.slots));
        } else if (kind === "scrollTo") {
          scrollToFrameOffset(data?.y, !!data?.reveal);
        } else if (kind === "navigate") {
          openLink(data);
        } else if (kind === DESIGNER_MESSAGE_KIND && designerEventRef.current) {
          designerEventRef.current(data);
        }
      };
      view.addEventListener("message", onMessage);
      return () => view.removeEventListener("message", onMessage);
    }, [srcDoc, minHeight, maxHeight, openLink, scrollToFrameOffset]);

    return (
      <div className={`hwdi-sandbox-frame-wrap ${className}`.trim()} ref={wrapRef}>
        <div className="hwdi-sandbox-frame-box">
          <iframe
            ref={iframeRef}
            title={title}
            // allow-scripts: the tutorial's own customJs. allow-popups(+escape):
            // a link the shim could not hand over still opens a normal tab.
            // Deliberately NOT allow-same-origin -- never add it here.
            sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
            srcDoc={srcDoc}
            onLoad={() => {
              if (!designer) return;
              postToFrame({ action: "designer:select", blockId: selectedRef.current });
              // The frame re-renders after each change: keep the keyboard in it
              // (arrow keys) when it had it.
              const frameEl = iframeRef.current;
              if (frameEl && frameEl.ownerDocument.activeElement === frameEl) postToFrame({ action: "designer:focus" });
            }}
            className="hwdi-sandbox-frame"
            style={{ height: frameHeight }}
          />
          {/* Visual editing: the players stay hidden so a video block can be
              selected and moved like any other (its slot shows the poster). */}
          {slots.length > 0 && !designEdit && (
            <div className="hwdi-video-overlay-layer" aria-hidden={false}>
              {slots.map((slot) => (
                <div
                  key={slot.key}
                  className="hwdi-video-overlay"
                  style={{ left: slot.x, top: slot.y, width: slot.w, height: slot.h }}
                >
                  <iframe
                    src={buildVideoEmbedUrl(slot.ref, { autoplay: slot.autoplay, muted: slot.muted, controls: slot.controls, start: slot.ref.start })}
                    title={slot.title || elakayAutoText("h_ffbaf58f123162", "video")}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        {errorCount > 0 && (
          <div className="hwdi-sandbox-frame-errors" role="status">
            <AlertTriangle size={13} />
            {elakayAutoText(
              errorCount === 1 ? "runtimeErrorOne" : "runtimeErrorMany",
              errorCount === 1 ? "{value0} runtime error in this page's script." : "{value0} runtime errors in this page's script.",
              { value0: errorCount }
            )}
          </div>
        )}
      </div>
    );
  }
);

export default HowWeDoItSandboxedFrame;
